// src/utils/providerUtils.js
import { EventEmitter } from 'events';
import { ethers } from 'ethers';

/**
 * Create a simplified Ethereum provider that mirrors MetaMask's API
 * @param {Object} wallet - Current wallet object
 * @param {Object} chain - Current chain object
 * @param {Object} provider - Ethers provider
 * @param {Function} requestCallback - Callback for handling provider requests
 * @returns {Object} - Injected provider object
 */
export const injectEthereumProvider = (wallet, chain, provider, requestCallback) => {
  if (!wallet || !chain || !provider) {
    throw new Error('Wallet, chain, and provider are required');
  }
  
  // Create event emitter for provider events
  const eventEmitter = new EventEmitter();
  
  // Create the injected provider object
  const injectedProvider = {
    isMetaMask: true,
    isBitDog: true,
    isConnected: () => true,
    chainId: ethers.toQuantity(chain.chainId),
    networkVersion: chain.chainId.toString(),
    selectedAddress: wallet.address,
    
    // Basic RPC methods
    request: async (request) => {
      console.log('Provider request:', request);
      
      // Simple methods that don't require user approval
      if (request.method === 'eth_chainId') {
        return injectedProvider.chainId;
      }
      
      if (request.method === 'net_version') {
        return injectedProvider.networkVersion;
      }
      
      if (request.method === 'eth_accounts' || request.method === 'eth_requestAccounts') {
        return [injectedProvider.selectedAddress];
      }
      
      // For other methods, forward to the request handler
      if (requestCallback) {
        // This will be asynchronous - results will come back via event emitter
        requestCallback({
          id: Date.now().toString(),
          method: request.method,
          params: request.params || [],
          origin: window.location.origin
        });
        
        // Return a promise that resolves when we get a response
        return new Promise((resolve, reject) => {
          const responseHandler = (response) => {
            // Check if this is the response for our request
            if (response.id === request.id) {
              // Remove the listener
              eventEmitter.removeListener('response', responseHandler);
              
              if (response.error) {
                reject(new Error(response.error.message));
              } else {
                resolve(response.result);
              }
            }
          };
          
          eventEmitter.on('response', responseHandler);
          
          // Set a timeout to reject the promise after 60 seconds
          setTimeout(() => {
            eventEmitter.removeListener('response', responseHandler);
            reject(new Error('Request timeout'));
          }, 60000);
        });
      }
      
      throw new Error('Request method not supported');
    },
    
    // Legacy RPC method
    sendAsync: (request, callback) => {
      injectedProvider.request(request)
        .then(result => {
          callback(null, {
            id: request.id,
            jsonrpc: '2.0',
            result
          });
        })
        .catch(error => {
          callback(error);
        });
    },
    
    // Legacy send method
    send: (methodOrRequest, paramsOrCallback) => {
      // Handle different call signatures
      if (typeof methodOrRequest === 'string') {
        // This is the old-style API
        const method = methodOrRequest;
        const params = Array.isArray(paramsOrCallback) ? paramsOrCallback : [];
        
        return injectedProvider.request({ method, params });
      } else if (typeof methodOrRequest === 'object' && typeof paramsOrCallback === 'function') {
        // This is sendAsync
        injectedProvider.sendAsync(methodOrRequest, paramsOrCallback);
      } else {
        // This is sync send (not really supported in modern providers)
        throw new Error('Synchronous send is not supported');
      }
    },
    
    // Event handlers
    on: (event, listener) => {
      eventEmitter.on(event, listener);
      return injectedProvider;
    },
    
    once: (event, listener) => {
      eventEmitter.once(event, listener);
      return injectedProvider;
    },
    
    removeListener: (event, listener) => {
      eventEmitter.removeListener(event, listener);
      return injectedProvider;
    },
    
    removeAllListeners: (event) => {
      eventEmitter.removeAllListeners(event);
      return injectedProvider;
    },
    
    // For internal use - emit events
    emit: (event, ...args) => {
      eventEmitter.emit(event, ...args);
    },
    
    // Add access to the original provider and wallet for internal use
    _provider: provider,
    _wallet: wallet,
    _chain: chain
  };
  
  return injectedProvider;
};

/**
 * Create the injection script to be injected into target pages
 * @returns {string} - Script content
 */
export const createInjectionScript = () => {
  // This script will be injected into the page to intercept window.ethereum calls
  return `
    (function() {
      // Save reference to any existing providers
      const originalEthereum = window.ethereum;
      
      // Create a message channel for secure communication with the parent
      const channel = new MessageChannel();
      const port = channel.port1;
      
      // Listen for messages from the parent (our wallet)
      window.addEventListener('message', (event) => {
        // Only accept messages from the parent wallet
        if (event.source !== window.parent) return;
        
        // Handle provider injection
        if (event.data && event.data.type === 'BITDOG_PROVIDER_INJECTION') {
          const providerConfig = event.data.provider;
          
          // Create our provider proxy
          const bitdogProvider = {
            isMetaMask: true,
            isBitDog: true,
            isConnected: () => providerConfig.isConnected,
            chainId: providerConfig.chainId,
            networkVersion: providerConfig.networkVersion,
            selectedAddress: providerConfig.selectedAddress,
            
            // Request method that forwards to the parent wallet
            request: async (request) => {
              return new Promise((resolve, reject) => {
                const requestId = Date.now().toString();
                
                // Create listener for this specific request
                const responseListener = (event) => {
                  if (event.data && event.data.type === 'BITDOG_PROVIDER_RESPONSE' && event.data.id === requestId) {
                    window.removeEventListener('message', responseListener);
                    
                    if (event.data.error) {
                      reject(new Error(event.data.error.message));
                    } else {
                      resolve(event.data.result);
                    }
                  }
                };
                
                window.addEventListener('message', responseListener);
                
                // Send request to the parent wallet
                window.parent.postMessage({
                  type: 'BITDOG_PROVIDER_REQUEST',
                  id: requestId,
                  request
                }, '*');
                
                // Set timeout for request
                setTimeout(() => {
                  window.removeEventListener('message', responseListener);
                  reject(new Error('Request timeout'));
                }, 60000);
              });
            },
            
            // Legacy methods
            sendAsync: (request, callback) => {
              bitdogProvider.request(request)
                .then(result => {
                  callback(null, {
                    id: request.id,
                    jsonrpc: '2.0',
                    result
                  });
                })
                .catch(error => {
                  callback(error);
                });
            },
            
            send: (methodOrRequest, paramsOrCallback) => {
              // Handle different call signatures
              if (typeof methodOrRequest === 'string') {
                const method = methodOrRequest;
                const params = Array.isArray(paramsOrCallback) ? paramsOrCallback : [];
                
                return bitdogProvider.request({ method, params });
              } else if (typeof methodOrRequest === 'object' && typeof paramsOrCallback === 'function') {
                bitdogProvider.sendAsync(methodOrRequest, paramsOrCallback);
              } else {
                throw new Error('Synchronous send is not supported');
              }
            },
            
            // Event handling
            _events: {},
            
            on: (eventName, listener) => {
              if (!bitdogProvider._events[eventName]) {
                bitdogProvider._events[eventName] = [];
              }
              bitdogProvider._events[eventName].push(listener);
              return bitdogProvider;
            },
            
            once: (eventName, listener) => {
              const onceListener = (...args) => {
                bitdogProvider.removeListener(eventName, onceListener);
                listener.apply(bitdogProvider, args);
              };
              return bitdogProvider.on(eventName, onceListener);
            },
            
            removeListener: (eventName, listener) => {
              if (bitdogProvider._events[eventName]) {
                bitdogProvider._events[eventName] = bitdogProvider._events[eventName]
                  .filter(l => l !== listener);
              }
              return bitdogProvider;
            },
            
            removeAllListeners: (eventName) => {
              if (eventName) {
                bitdogProvider._events[eventName] = [];
              } else {
                bitdogProvider._events = {};
              }
              return bitdogProvider;
            },
            
            // Internal method to emit events
            _emit: (eventName, ...args) => {
              if (bitdogProvider._events[eventName]) {
                bitdogProvider._events[eventName].forEach(listener => {
                  listener.apply(bitdogProvider, args);
                });
              }
            }
          };
          
          // Override window.ethereum while preserving original
          Object.defineProperty(window, 'ethereum', {
            value: bitdogProvider,
            writable: true,
            configurable: true
          });
          
          // Emit connect event
          setTimeout(() => {
            if (bitdogProvider._events['connect']) {
              bitdogProvider._emit('connect', { chainId: bitdogProvider.chainId });
            }
          }, 10);
          
          // Also set as window.bitdog
          window.bitdog = bitdogProvider;
          
          console.log('BitDog Wallet provider injected');
        }
        
        // Handle provider events
        if (event.data && event.data.type === 'BITDOG_PROVIDER_EVENT') {
          const { eventName, args } = event.data;
          
          if (window.ethereum && window.ethereum.isBitDog) {
            window.ethereum._emit(eventName, ...args);
          }
        }
      });
      
      // Notify wallet that we're ready for injection
      window.parent.postMessage({ type: 'BITDOG_PROVIDER_REQUEST' }, '*');
    })();
  `;
};

/**
 * Inject the provider script into a web page
 * @param {HTMLIFrameElement} iframe - Target iframe
 */
export const injectProviderScript = (iframe) => {
    try {
      if (!iframe || !iframe.contentWindow || !iframe.contentDocument) {
        console.error('Invalid iframe reference for injection');
        return;
      }
      
      console.log('Attempting to inject provider script into iframe');
      
      // First, inject a script to fix touch handling
      const touchFixScript = iframe.contentDocument.createElement('script');
      touchFixScript.textContent = `
        // Fix touch handling in iframe content
        (function() {
          console.log('Applying touch behavior fix to iframe content');
          
          // Remove any inherited touch restrictions from parent document
          document.documentElement.style.touchAction = 'auto';
          document.body.style.touchAction = 'auto';
          
          // Allow zoom and all standard touch behaviors by modifying viewport meta
          const meta = document.querySelector('meta[name="viewport"]');
          if (meta) {
            // Modify existing viewport tag to allow user scaling
            meta.content = meta.content
              .replace(/user-scalable=no/gi, 'user-scalable=yes')
              .replace(/maximum-scale=[0-9.]+/gi, 'maximum-scale=5.0')
              .replace(/minimum-scale=[0-9.]+/gi, 'minimum-scale=0.1');
            console.log('Modified viewport meta tag:', meta.content);
          } else {
            // Create viewport meta tag if it doesn't exist
            const newMeta = document.createElement('meta');
            newMeta.name = 'viewport';
            newMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=yes';
            document.head.appendChild(newMeta);
            console.log('Added viewport meta tag');
          }
          
          // Fix touch handling for all interactive elements
          const clickableElements = document.querySelectorAll(
            'a, button, input, select, textarea, [role="button"], [tabindex]'
          );
          clickableElements.forEach(el => {
            el.style.touchAction = 'auto';
            el.style.cursor = 'pointer';
          });
          
          // If using Hammer.js or similar libraries, tell them not to prevent default
          if (window.Hammer) {
            window.Hammer.defaults.touchAction = 'auto';
            window.Hammer.defaults.cssProps.userSelect = 'auto';
          }
          
          console.log('Touch behavior fix applied to iframe content');
        })();
      `;
      
      // Inject the touch fix script
      const head = iframe.contentDocument.head || iframe.contentDocument.getElementsByTagName('head')[0];
      if (head) {
        head.appendChild(touchFixScript);
        console.log('Touch fix script injected successfully');
      }
      
      // Create provider script element
      const script = iframe.contentDocument.createElement('script');
      script.textContent = createInjectionScript();
      
      // Inject provider script into page
      if (!head) {
        console.error('Could not find document head for script injection');
        return;
      }
      
      head.appendChild(script);
      console.log('Provider script injected successfully');
      
      // Add diagnostic script to check if ethereum object is available
      const diagnosticScript = iframe.contentDocument.createElement('script');
      diagnosticScript.textContent = `
        console.log('BitDog diagnostic: window.ethereum available:', !!window.ethereum);
        if (window.ethereum) {
          console.log('Properties:', Object.keys(window.ethereum));
        }
        
        // Check touch handling
        console.log('Touch action body:', getComputedStyle(document.body).touchAction);
        console.log('Touch action html:', getComputedStyle(document.documentElement).touchAction);
        console.log('Viewport meta:', document.querySelector('meta[name="viewport"]')?.content);
      `;
      head.appendChild(diagnosticScript);
    } catch (error) {
      console.error('Error injecting provider script:', error);
    }
  };